import React, { useState, useEffect } from "react";
import MoreIcon from "@mui/icons-material/MoreVert";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Badge from "@mui/material/Badge";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import AppBar from "@mui/material/AppBar";
import Avatar from "@mui/material/Avatar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { Link } from "react-router-dom";
import CustomButton from "../button/button";
import logo from "../../../images/phase-3/stirrup_logo/stirrup-logo.svg";
import DefaultUser from "../../../images/Default_user.svg";
import OfficeLogo from "../../../images/Office.svg";
import ProfileCard from "../../widgets/phase-3/profileCard/profileCard";
import UserProfileCard from "../../widgets/phase-3/userProfileCard/userProfileCard";
import EntityPopupMenu from "../../widgets/phase-3/EntityPopupMenu/EntityPopupMenu";
import moment from "moment";
import AppBarUseStyles from "./AppBarStyles";
import { useSelector, useDispatch } from "react-redux";

// React Query
import { useQuery } from "@tanstack/react-query";

// Import notification action
import { getNotificationsList } from "../../../actions/esgCompliance/esgComplianceAction";

// Import Notification Drawer Component
import NotificationDrawer from "../../widgets/phase-3/notifications/NotificationDrawer";

function CustomAppBar({
  isOpen,
  userName,
  userEmail,
  login,
  token,
  logOut,
  organisation_details,
  parent_organisation,
  user_type,
  loginDetails,
  getNotifications,
  setNotification,
  setTotalNotification,
}) {
  const dispatch = useDispatch();
  const classes = AppBarUseStyles();

  // Get surveyListing from Redux
  const surveyListing = useSelector((state) => state.surveyListing);
  const { user } = useSelector((state) => state);
  // const navigate = useNavigate();
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = React.useState(null);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const { parent_entity } = useSelector((state) => state.orgDetails);

  // Pagination state
  const [page, setPage] = React.useState({
    starting_after: 0,
    limit: 10, // Changed from 20 to 10
  });
  const [accumulatedNotifications, setAccumulatedNotifications] =
    React.useState([]);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [hasMoreNotifications, setHasMoreNotifications] = React.useState(true);
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);

  const { profileDetails } = useSelector((state) => state?.generalSettings);

  // Map tab value to filter
  const getFilterFromTab = (tab) => {
    if (tab === 0) return "all";
    if (tab === 1) return "unread";
    if (tab === 2) return "mentions";
    return "all";
  };

  // React Query to fetch unread count (always active)
  const { data: unreadData } = useQuery({
    queryKey: ["notificationsUnreadCount"],
    queryFn: async () => {
      // console.log("🔔 Fetching unread count...");
      const result = await getNotificationsList({
        filter: "unread",
        limit: 1,
        skip: 0,
      });
      // console.log("🔢 Unread Count:", result?.unreadCount);
      return result;
    },
    enabled: true, // Always fetch unread count
    staleTime: 2 * 60 * 1000, // Keep data fresh for 2 minutes (no refetch during this time)
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false,
    refetchInterval: 60000, // Refetch every 60 seconds (reduced from 30s)
  });

  // React Query to fetch notifications list (only when drawer is open)
  const {
    data: notificationsData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["notificationsList", tabValue, page.starting_after],
    queryFn: async () => {
      const filter = getFilterFromTab(tabValue);
      // console.log(
      //   "📋 Fetching notifications with filter:",
      //   filter,
      //   "for tab:",
      //   tabValue,
      //   "starting_after:",
      //   page.starting_after
      // );
      const result = await getNotificationsList({
        filter,
        limit: page.limit,
        skip: page.starting_after,
      });
      // console.log("✅ Notifications API Response:", result);
      // console.log("📋 Notifications List:", result?.result);

      // Accumulate notifications
      if (page.starting_after === 0) {
        // First load - replace all notifications
        setAccumulatedNotifications(result?.result || []);
        setIsInitialLoad(false);
      } else {
        // Load more - append to existing
        setAccumulatedNotifications((prev) => [
          ...prev,
          ...(result?.result || []),
        ]);
      }

      // Check if there are more notifications
      const fetchedCount = result?.result?.length || 0;
      setHasMoreNotifications(fetchedCount >= page.limit);

      return result;
    },
    enabled: isDrawerOpen, // Only fetch full list when drawer is open
    staleTime: 0, // Always fetch fresh data on tab change (no caching between tabs)
    cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnWindowFocus: false,
    refetchInterval: false, // Disable auto-refetch to prevent resetting pagination
  });

  const notifications = accumulatedNotifications;
  const unreadCount = unreadData?.unreadCount || 0;

  // console.log("📊 Current notifications state:", {
  //   isDrawerOpen,
  //   tabValue,
  //   isLoading,
  //   notificationsCount: notifications.length,
  //   unreadCount,
  // });
  // console.log(profileDetails);

  function handleUserProfileCard() {
    setAnchorElUser(null);
  }

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuUser = (event) => {
    if (event.currentTarget !== anchorElUser) {
      setAnchorElUser(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerClose = () => {
    setNotification([]);
    setTotalNotification(0);
    setIsDrawerOpen(!isDrawerOpen);
    // Reset pagination state
    setPage({
      starting_after: 0,
      limit: 10,
    });
    setAccumulatedNotifications([]);
    setHasMoreNotifications(true);
    setIsInitialLoad(true);
  };

  const OpenNotificationDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
    if (!isDrawerOpen) {
      // Reset accumulated notifications when opening drawer
      setAccumulatedNotifications([]);
      setPage({
        starting_after: 0,
        limit: 10,
      });
      setIsInitialLoad(true);
    }
  };

  const handleLoadMoreNotifications = async () => {
    if (isLoadingMore || !hasMoreNotifications) return;

    setIsLoadingMore(true);
    try {
      // Increment starting_after to fetch next page
      const newStartingAfter = page.starting_after + page.limit;
      setPage({
        ...page,
        starting_after: newStartingAfter,
      });

      // The query will automatically refetch with the new starting_after value
      await refetch();
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Reset pagination when tab changes
  useEffect(() => {
    setAccumulatedNotifications([]);
    setPage({
      starting_after: 0,
      limit: 10,
    });
    setHasMoreNotifications(true);
    setIsInitialLoad(true);
  }, [tabValue]);
  const mobileMenuId = "primary-search-account-menu-mobile";
  // React.useEffect(() => {
  //   let selectedORG = undefined;
  //   if (organisation_details) {
  //     selectedORG = organisation_details.find(
  //       (ele) => ele._id === localStorage.getItem("current_organisation")
  //     );
  //   }
  //   if (selectedORG) setSelectedOraganization(selectedORG);
  // setSelectedOraganization(selectedORG);
  // localStorage.setItem("status", selectedORG?.payment_status);
  // localStorage.setItem("status_message", selectedORG?.payment_message);
  // setIsSubscriptionActive(selectedORG?.payment_status === "EXPIRED" ? false : true);
  // }, [organisation_details]);

  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      <MenuItem onClick={() => handleMobileMenuClose()}>
        <IconButton size="large">
          <Icon>home</Icon>
        </IconButton>
        <p>Home</p>
      </MenuItem>
      <MenuItem onClick={() => handleMobileMenuClose()}>
        <IconButton size="large">
          <Badge
            badgeContent={unreadCount}
            overlap="circular"
            color="secondary"
            max={99}
          >
            <Icon>notifications</Icon>
          </Badge>
        </IconButton>
        <p>Notifications</p>
      </MenuItem>
    </Menu>
  );

  // const notificationDrawer = () => {
  //   return (
  //     <Drawer anchor="right" open={isDrawerOpen} onClose={handleDrawerClose}>
  //       <div className={classes.mainDiv}>
  //         <Grid
  //           container
  //           justifyContent="center"
  //           className={classes.headerGrid}
  //         >
  //           <Grid item className={classes.subGrid}>
  //             <Typography variant="body1">Notification Panel</Typography>
  //           </Grid>
  //         </Grid>
  //         <Grid container spacing={1} style={{ marginTop: 25 }}>
  //           {surveyListing.notifications.length > 0 ? (
  //             surveyListing.notifications.map((item, index) => (
  //               <Grid item xs={12} key={index}>
  //                 <List>
  //                   <ListItem
  //                     key={item._id}
  //                     className={classes.notificationItem}
  //                   >
  //                     <ListItemIcon>
  //                       <Avatar
  //                         className={classes.avatar}
  //                         src={
  //                           item.created_by?.profile_img === ""
  //                             ? DefaultUser
  //                             : item.created_by?.profile_img?.url
  //                         }
  //                       ></Avatar>
  //                     </ListItemIcon>
  //                     <ListItemText
  //                       primary={
  //                         item.assessment_data.assessment_name !== ""
  //                           ? item.notification +
  //                             " in " +
  //                             item.assessment_data.assessment_name
  //                           : item.notification
  //                       }
  //                       secondary={
  //                         moment(item.created_on).format("DD/MM/YYYY") +
  //                         " " +
  //                         moment(item.created_on).format("HH:mm A")
  //                       }
  //                     />
  //                   </ListItem>
  //                 </List>
  //               </Grid>
  //             ))
  //           ) : (
  //             <div style={{ width: "100%" }}>
  //               <Typography style={{ textAlign: "center" }}>
  //                 No Notifications
  //               </Typography>
  //             </div>
  //           )}
  //           {surveyListing.totalNotifications > 10 &&
  //             surveyListing.totalNotifications >= page.starting_after && (
  //               <CustomButton
  //                 onClick={() => handleLoadMore()}
  //                 color="primary"
  //                 fullWidth
  //                 variant="contained"
  //               >
  //                 Load More
  //               </CustomButton>
  //             )}
  //         </Grid>
  //         {/* <Grid container justifyContent="center">
  //           <Grid
  //             item
  //             xs={6}
  //             className={classes.activitiesGrid}
  //           >
  //             <Typography
  //               onClick={handleShowMoreNotification}
  //               className={classes.activityTypo}
  //             >
  //               Load more activities
  //             </Typography>
  //           </Grid>
  //         </Grid> */}
  //       </div>
  //     </Drawer>
  //   );
  // };
  return (
    <div className={classes.grow}>
      {/* Notification Drawer */}
      <NotificationDrawer
        open={isDrawerOpen}
        onClose={handleDrawerClose}
        notifications={notifications}
        unreadCount={unreadCount}
        isLoading={isInitialLoad}
        currentTab={tabValue}
        onTabChange={setTabValue}
        onLoadMore={handleLoadMoreNotifications}
        hasMore={hasMoreNotifications}
        isLoadingMore={isLoadingMore}
      />

      <AppBar position="fixed" color="inherit">
        <Grid container justifyContent="center">
          <Grid item xs={12} sm={11}>
            <Toolbar>
              {!isOpen &&
                loginDetails?.org_details &&
                loginDetails?.org_details?.after_login &&
                loginDetails?.org_details?.after_login?.logo && (
                  <div
                    style={
                      loginDetails?.org_details &&
                      loginDetails?.org_details?.after_login?.logo
                        ? loginDetails.org_details?.after_login?.logo_styles
                        : JSON.parse(localStorage.getItem("logo_styles"))
                          ? JSON.parse(localStorage.getItem("logo_styles"))
                          : {}
                    }
                  >
                    {/* <div style={{ width:'175px', height:'60px',marginTop:'8px'}}> */}
                    <Link
                      to={
                        user_type === "SA"
                          ? "/survey_listing"
                          : "/report_listing"
                      }
                    >
                      <img
                        style={{
                          width: "70%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                        src={
                          loginDetails?.org_details &&
                          loginDetails?.org_details?.after_login?.logo
                            ? loginDetails?.org_details?.after_login?.logo
                            : localStorage.getItem("logo") &&
                              localStorage.getItem("logo")
                        }
                        alt="logo"
                      />
                    </Link>
                  </div>
                )}
              {isOpen && (
                <div>
                  {/* <div style={{ width:'175px', height:'60px',marginTop:'8px'}}> */}
                  <Link
                    to={
                      user_type === "SA" ? "/survey_listing" : "/report_listing"
                    }
                    style={{ textDecoration: "none" }}
                  >
                    <img
                      style={{
                        width: "70%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                      src={logo}
                      alt="logo"
                    />
                  </Link>
                </div>
              )}
              <div className={classes.grow} />
              <div
                className={classes.sectionDesktop}
                data-tut="reactour__profile_card"
              >
                <IconButton
                  name="notification"
                  onClick={OpenNotificationDrawer}
                  size="large"
                >
                  <Badge
                    badgeContent={unreadCount}
                    overlap="circular"
                    color="secondary"
                    max={99}
                  >
                    <Icon>notifications</Icon>
                  </Badge>
                </IconButton>

                {!login && (
                  <>
                    <Link to="/login" style={{ textDecoration: "none" }}>
                      <CustomButton
                        style={{ marginTop: 5 }}
                        color="primary"
                        variant="contained"
                      >
                        Login
                      </CustomButton>
                    </Link>
                    &nbsp; &nbsp;
                    <Link to="/signup" style={{ textDecoration: "none" }}>
                      <CustomButton
                        style={{ marginTop: 5 }}
                        color="primary"
                        variant="contained"
                      >
                        signup
                      </CustomButton>
                    </Link>
                  </>
                )}
                {login && (
                  <>
                    <div
                      className={classes.userImage}
                      onClick={handleMenuUser}
                      data-tut="reactour__profile_user"
                    >
                      <Avatar
                        size="small"
                        style={{ width: 35, height: 35 }}
                        src={
                          (!!profileDetails?.profile_img &&
                            profileDetails?.profile_img?.url) ||
                          (!!user?.profile_img && user?.profile_img?.url)
                            ? profileDetails?.profile_img?.url ||
                              user?.profile_img?.url
                            : DefaultUser
                        }
                      ></Avatar>
                      <UserProfileCard
                        anchorEl={anchorElUser}
                        onClose={handleUserProfileCard}
                        userName={userName}
                        userEmail={userEmail}
                        token={token}
                        logOut={logOut}
                        loginDetails={loginDetails}
                      />
                    </div>
                    <div className={classes.border}>
                      <div
                        className={classes.name}
                        onClick={handleMenu}
                        data-tut="reactour__profile"
                      >
                        <Typography
                          variant="subtitle2"
                          style={{ marginRight: "20px" }}
                        >
                          {(!!profileDetails?.org_details &&
                            profileDetails?.org_details?.entity_name) ||
                            (!!user?.org_details?.entity_name &&
                              user?.org_details?.entity_name)}
                        </Typography>
                        <Avatar
                          size="small"
                          className={classes.small}
                          src={
                            (!!profileDetails?.org_details?.profile_img &&
                              profileDetails?.org_details?.profile_img?.url) ||
                            (!!user?.org_details?.profile_img &&
                              user?.org_details?.profile_img?.url)
                              ? profileDetails?.org_details?.profile_img?.url ||
                                user?.org_details?.profile_img?.url
                              : parent_entity?.profile_img?.url || OfficeLogo
                          }
                        ></Avatar>
                      </div>
                      {(user?.parent_id === user?.entity_id ||
                        user?.parent_id === user?.unit_id) && (
                        <EntityPopupMenu
                          anchorEl={anchorEl}
                          open={
                            user?.parent_id === user?.entity_id ||
                            user?.parent_id === user?.unit_id
                              ? open
                              : false
                          }
                          onClose={handleClose}
                          profileDetails={profileDetails}
                          user={user}
                          selectedEntity={selectedEntity}
                          onEntitySelect={(entityName, entityType) => {
                            // Handle entity selection
                            
                            setSelectedEntity({
                              name: entityName,
                              type: entityType,
                            });
                          }}
                        />
                      )}
                    </div>
                  </>
                )}
              </div>
              <div className={classes.sectionMobile}>
                <IconButton
                  aria-label="show more"
                  aria-controls={mobileMenuId}
                  aria-haspopup="true"
                  onClick={handleMobileMenuOpen}
                  color="inherit"
                  size="large"
                >
                  <MoreIcon />
                </IconButton>

                {login === false && (
                  <>
                    <CustomButton color="primary" variant="contained">
                      Login
                    </CustomButton>
                  </>
                )}
              </div>
            </Toolbar>
          </Grid>
        </Grid>
      </AppBar>
      {renderMobileMenu}
    </div>
  );
}
export default React.memo(CustomAppBar);
